/* pdp-buy-box.js — PDP Buy Box Redesign
   ES6 class encapsulated in an IIFE.
   Reads config from <script type="application/json" data-buybox-config>.
   Discount contract: line-item properties _offer-page-landing / _offer-page-items /
   _offer-page-discount are consumed by the live `offer-page-discount` Shopify Function.
   DO NOT rename these properties. */

(() => {
  'use strict';

  class PdpBuyBox {
    constructor(root) {
      this.root = root;
      this.isSubmitting = false;
      this.config = this._parseConfig();
      if (!this.config) return;

      this._cacheDom();
      this._init();
    }

    // ── Setup ──────────────────────────────────────────────────────────────

    _parseConfig() {
      const el = this.root.querySelector('[data-buybox-config]');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch {
        console.error('[pdp-buy-box] Failed to parse config JSON.');
        return null;
      }
    }

    _cacheDom() {
      const r = this.root;
      this.pdpBuyBox = r.querySelector('pdp-buy-box');
      this.oneTimeRadio = r.querySelector('#pdp-buy-box-input-one-time');
      this.subRadio = r.querySelector('#pdp-buy-box-input-subscribe');
      this.sellingPlanSelect = r.querySelector('.pdp-buy-box__select-selling-plans');
      this.headingPriceOneTime = r.querySelector('[data-buybox-heading-price-onetime]');
      this.headingPriceSub = r.querySelector('[data-buybox-heading-price-sub]');
      this.alertBanner = r.querySelector('.alert-banner');
      this.alertText = r.querySelector('.alert-banner__text');
      this.addToCartBtn = r.querySelector('.pdp-buy-box-add-to-cart');
      this.progressLine = r.querySelector('.benefits-progress-bar__progress-line--progress');
      this.progressRewards = Array.from(r.querySelectorAll('.benefits-progress-bar__reward'));
      this.oneTimeQtyRadios = Array.from(r.querySelectorAll('input[name="one-time-option-quantity"]'));
      this.subQtyRadios = Array.from(r.querySelectorAll('input[name="subscription-option-quantity"]'));
      this.oneTimeFlavours = Array.from(r.querySelectorAll('.pdp-buy-box__content-one-time .flavour-item'));
      this.subFlavours = Array.from(r.querySelectorAll('.pdp-buy-box__content-subscribe .flavour-item'));
    }

    _init() {
      this._resetToOneTime();
      this._bindModeSwitch();
      this._bindPackageTiers();
      this._bindFlavourItems();
      if (this.sellingPlanSelect) {
        this.sellingPlanSelect.addEventListener('change', () => this._syncState());
      }
      this._bindAddToCart();
      this._syncState();
    }

    // ── State ──────────────────────────────────────────────────────────────

    _getCurrentState() {
      const isSubscription = this.subRadio?.checked ?? false;
      const qtyName = isSubscription ? 'subscription-option-quantity' : 'one-time-option-quantity';
      const checked = this.root.querySelector(`input[name="${qtyName}"]:checked`);
      const quantity = checked ? (Number(checked.dataset.variantQuantity) || 1) : 1;
      return { isSubscription, quantity };
    }

    _resetToOneTime() {
      if (!this.oneTimeRadio || !this.subRadio) return;
      this.subRadio.checked = false;
      this.oneTimeRadio.checked = true;
      const defaultQty = this.root.querySelector('#one-bag-one-time-option');
      if (defaultQty) defaultQty.checked = true;
      this.pdpBuyBox?.classList.add('pdp-buy-box--mode-onetime');
      this.pdpBuyBox?.classList.remove('pdp-buy-box--mode-subscription');
    }

    // ── Mode switch ────────────────────────────────────────────────────────

    _bindModeSwitch() {
      const updateMode = () => {
        const isSub = this.subRadio?.checked;
        this.pdpBuyBox?.classList.toggle('pdp-buy-box--mode-subscription', isSub);
        this.pdpBuyBox?.classList.toggle('pdp-buy-box--mode-onetime', !isSub);
        this._syncState();
      };
      this.oneTimeRadio?.addEventListener('change', updateMode);
      this.subRadio?.addEventListener('change', updateMode);
    }

    // ── Package tiers ──────────────────────────────────────────────────────

    _bindPackageTiers() {
      const syncAndTrim = (radio) => {
        const value = radio.dataset.variantQuantity;
        this._syncLinkedQty(value);
        this._trimFlavoursToPackage(Number(value));
        this._syncState();
      };
      [...this.oneTimeQtyRadios, ...this.subQtyRadios].forEach((radio) => {
        radio.addEventListener('change', () => syncAndTrim(radio));
      });
    }

    _syncLinkedQty(value) {
      [...this.oneTimeQtyRadios, ...this.subQtyRadios].forEach((radio) => {
        radio.checked = radio.dataset.variantQuantity === value;
      });
    }

    // ── Flavour steppers ───────────────────────────────────────────────────

    _bindFlavourItems() {
      this.oneTimeFlavours.forEach((item, i) => this._bindStepper(item, i, this.subFlavours));
      this.subFlavours.forEach((item, i) => this._bindStepper(item, i, this.oneTimeFlavours));
    }

    _bindStepper(item, index, counterparts) {
      const input = item.querySelector('.flavour-item__quantity-input');
      const minusBtn = item.querySelector('.flavour-item__quantity-button--minus');
      const plusBtn = item.querySelector('.flavour-item__quantity-button--plus');
      if (!input || !minusBtn || !plusBtn) return;

      this._refreshMinusBtn(input, minusBtn);

      plusBtn.addEventListener('click', () => {
        const val = Number(input.value);
        if (val < Number(input.max || 99)) {
          this._setInputValue(input, val + 1);
          this._refreshMinusBtn(input, minusBtn);
          this._syncCounterpart(counterparts, index, input.value);
          this._autoSelectPackage();
          this._syncState();
        }
      });

      minusBtn.addEventListener('click', () => {
        const val = Number(input.value);
        if (val > Number(input.min || 0)) {
          this._setInputValue(input, val - 1);
          this._refreshMinusBtn(input, minusBtn);
          this._syncCounterpart(counterparts, index, input.value);
          this._syncState();
        }
      });
    }

    _setInputValue(input, value) {
      input.value = String(value);
      input.setAttribute('value', String(value));
    }

    _refreshMinusBtn(input, minusBtn) {
      minusBtn.disabled = Number(input.value) <= Number(input.min || 0);
    }

    _syncCounterpart(counterparts, index, value) {
      const cp = counterparts[index];
      if (!cp) return;
      const cpInput = cp.querySelector('.flavour-item__quantity-input');
      const cpMinus = cp.querySelector('.flavour-item__quantity-button--minus');
      if (cpInput) {
        this._setInputValue(cpInput, value);
        if (cpMinus) this._refreshMinusBtn(cpInput, cpMinus);
      }
    }

    _getTotalFlavours(fromOneTime = true) {
      const items = fromOneTime ? this.oneTimeFlavours : this.subFlavours;
      return items.reduce((sum, item) => {
        const input = item.querySelector('.flavour-item__quantity-input');
        return sum + (Number(input?.value) || 0);
      }, 0);
    }

    _trimFlavoursToPackage(maxTotal) {
      let excess = this._getTotalFlavours(true) - maxTotal;
      if (excess <= 0) return;

      const items = [...this.oneTimeFlavours].reverse();
      for (const item of items) {
        if (excess <= 0) break;
        const input = item.querySelector('.flavour-item__quantity-input');
        const minusBtn = item.querySelector('.flavour-item__quantity-button--minus');
        if (!input || Number(input.value) <= 0) continue;
        const reduce = Math.min(excess, Number(input.value));
        this._setInputValue(input, Number(input.value) - reduce);
        excess -= reduce;
        if (minusBtn) this._refreshMinusBtn(input, minusBtn);
      }

      this.oneTimeFlavours.forEach((item, i) => {
        const inp = item.querySelector('.flavour-item__quantity-input');
        if (inp) this._syncCounterpart(this.subFlavours, i, inp.value);
      });
    }

    _autoSelectPackage() {
      const total = this._getTotalFlavours(true);
      const currentQty = this._getCurrentState().quantity;
      if (total >= 4 && currentQty < 4) {
        this._syncLinkedQty('4');
      } else if (total >= 2 && currentQty < 2) {
        this._syncLinkedQty('2');
      }
    }

    // ── UI sync ────────────────────────────────────────────────────────────

    _syncState() {
      const state = this._getCurrentState();
      this._updateAlertAndButton(state);
      this._updateProgressBar(this._getTotalFlavours(true));
      this._updateHeadingPrices(state);
    }

    _updateAlertAndButton(state) {
      if (!this.alertBanner || !this.addToCartBtn) return;
      const total = this._getTotalFlavours(!state.isSubscription);
      const remaining = state.quantity - total;

      if (remaining > 0) {
        this.alertBanner.classList.remove('alert-banner--hidden');
        if (this.alertText) {
          const tmpl = this.config?.alertBannerText
            || 'Choose {count} more {flavour_word} to continue.';
          const flavourWord = remaining !== 1 ? 'flavours' : 'flavour';
          const msg = tmpl
            .replace('{count} more {flavour_word}', `<strong>${remaining} more ${flavourWord}</strong>`)
            .replace('{count}', `<strong>${remaining}</strong>`)
            .replace('{flavour_word}', flavourWord);
          this.alertText.innerHTML = msg;
        }
        this.addToCartBtn.disabled = true;
      } else {
        this.alertBanner.classList.add('alert-banner--hidden');
        this.addToCartBtn.disabled = false;
      }
    }

    _updateProgressBar(total) {
      const thresholds = [1, 2, 4];
      this.progressRewards.forEach((reward, i) => {
        reward.classList.toggle('benefits-progress-bar__reward--unlocked', total >= thresholds[i]);
      });
      if (this.progressLine) {
        const widths = ['0%', '25%', '50%', '75%', '100%'];
        this.progressLine.style.width = widths[Math.min(total, 4)];
      }
    }

    _updateHeadingPrices(state) {
      if (!this.config?.basePrice) return;
      const basePrice = this.config.basePrice;
      const qty = state.quantity;

      const oneTimeUnit = this._oneTimeUnitPrice(basePrice, qty);
      const subUnit = Math.round(oneTimeUnit * 0.85);

      if (this.headingPriceOneTime) {
        this.headingPriceOneTime.textContent = this._formatMoney(oneTimeUnit * qty);
      }
      if (this.headingPriceSub) {
        this.headingPriceSub.textContent = this._formatMoney(subUnit * qty);
      }
    }

    _oneTimeUnitPrice(basePrice, qty) {
      if (qty >= 4) return Math.round(basePrice * 0.8);
      if (qty >= 2) return Math.round(basePrice * 0.85);
      return basePrice;
    }

    _formatMoney(cents) {
      const fmt = this.config?.moneyWithCurrencyFormat
        ?? (typeof Shopify !== 'undefined' ? (Shopify.money_with_currency_format ?? Shopify.money_format) : null)
        ?? window.theme?.moneyWithCurrencyFormat;

      if (fmt) {
        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
          return Shopify.formatMoney(cents, fmt);
        }
        const amount = (cents / 100).toFixed(2);
        return fmt
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
          .replace(/\{\{\s*amount\s*\}\}/g, amount);
      }

      return `$${(cents / 100).toFixed(2)}`;
    }

    // ── Discount logic ─────────────────────────────────────────────────────

    _getDiscountForTotal(totalItems) {
      const tiers = (this.config?.discountTiers ?? []).slice().sort((a, b) => b.threshold - a.threshold);
      const match = tiers.find((t) => totalItems >= t.threshold);
      return match?.discount ?? 0;
    }

    // ── Selections ─────────────────────────────────────────────────────────

    _getSelections() {
      const state = this._getCurrentState();
      const flavours = state.isSubscription ? this.subFlavours : this.oneTimeFlavours;
      const selectedPlanName = this.sellingPlanSelect?.value ?? null;

      const allItems = flavours;
      const totalItems = allItems.reduce((sum, item) => {
        const input = item.querySelector('.flavour-item__quantity-input');
        return sum + (Number(input?.value) || 0);
      }, 0);
      const discount = this._getDiscountForTotal(totalItems);

      return allItems
        .map((item) => {
          const input = item.querySelector('.flavour-item__quantity-input');
          const qty = Number(input?.value) || 0;
          const variantId = Number(item.dataset.variantId) || 0;
          if (!variantId || qty === 0) return null;

          const entry = {
            id: variantId,
            quantity: qty,
            properties: {
              '_offer-page-landing': true,
              '_offer-page-items': totalItems,
              '_offer-page-discount': discount,
            },
          };

          if (state.isSubscription && selectedPlanName) {
            const plans = this.config?.flavourPlans?.[String(variantId)] ?? [];
            const plan = plans.find((p) => p.name === selectedPlanName);
            if (plan?.id) entry.selling_plan = plan.id;
          }

          return entry;
        })
        .filter(Boolean);
    }

    // ── Cart operations ────────────────────────────────────────────────────

    async _removeOfferPageItems() {
      const cart = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      }).then((r) => r.json());

      const toRemove = cart.items.filter(
        (item) =>
          item.properties?.['_offer-page-landing'] === true ||
          item.properties?.['_offer-page-landing'] === 'true'
      );
      if (!toRemove.length) return;

      const updates = {};
      toRemove.forEach((item) => {
        updates[item.key] = 0;
      });
      await this._postJSON('/cart/update.js', { updates });
    }

    async _postJSON(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) throw new Error(`[pdp-buy-box] Request failed: ${url} (${res.status})`);
      return res;
    }

    // ── Add to cart ────────────────────────────────────────────────────────

    _bindAddToCart() {
      this.addToCartBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._handleAddToCart();
      });
    }

    async _handleAddToCart() {
      if (this.isSubmitting) return;
      const selections = this._getSelections();
      if (!selections.length) return;

      this.isSubmitting = true;
      this._toggleLoading(true);
      document.dispatchEvent(new Event('onCartPending'));

      try {
        await this._removeOfferPageItems();
        await this._postJSON('/cart/add.js', { items: selections });
        document.dispatchEvent(new Event('onCartFinished'));
      } catch (err) {
        console.error('[pdp-buy-box] Add to cart failed:', err);
        document.dispatchEvent(new Event('onCartFinished'));
      } finally {
        this.isSubmitting = false;
        this._toggleLoading(false);
      }
    }

    _toggleLoading(state) {
      this.addToCartBtn?.classList.toggle('is-loading', state);
      if (this.addToCartBtn) this.addToCartBtn.disabled = state;
    }
  }

  // ── Init / section reload support ──────────────────────────────────────

  function init() {
    document.querySelectorAll('.container-pdp-buy-box').forEach((root) => {
      if (root.dataset.buyboxInit) return;
      root.dataset.buyboxInit = 'true';
      new PdpBuyBox(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('.container-pdp-buy-box').forEach((root) => {
      delete root.dataset.buyboxInit;
    });
    init();
  });
})();
